import { Button, Card, notification, Select, Skeleton, Tooltip, Typography } from 'antd'
import { useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faPenToSquare,
  faClipboardCheck,
  faCircleCheck,
  faFileInvoiceDollar,
  faEnvelope,
  faDollarSign,
  faArrowRight,
  faArrowsRotate,
} from '@fortawesome/pro-solid-svg-icons'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { centsToDollarsCompact, percentageDisplay } from '../../displayFormatters'
import { useInvoicingFunnel, useInvoicingFunnelRefresh } from './queries'
import {
  AdoptionDepthBucket,
  AdoptionDepthData,
  computeBucketSegments,
  ComputedStage,
  computePaymentsByJobTypeRows,
  computeStages,
  computeSummary,
  FunnelPeriod,
  FunnelPeriods,
  PaymentsByJobTypeData,
} from './funnelModel'
import './InvoicingFunnel.css'

const { Title, Text } = Typography

type StageConfig = {
  icon: IconDefinition
  color: string
}

const STAGE_CONFIG: Record<string, StageConfig> = {
  'Total Tenants':                 { icon: faUsers,              color: '#1890ff' },
  'Started Merchant Application':  { icon: faPenToSquare,        color: '#722ed1' },
  'Finished Merchant Application': { icon: faClipboardCheck,     color: '#fa8c16' },
  'Application Approved':          { icon: faCircleCheck,        color: '#52c41a' },
  'Created First Invoice':         { icon: faFileInvoiceDollar,  color: '#13c2c2' },
  'Sent First Invoice':            { icon: faEnvelope,           color: '#2f54eb' },
  'First Payment Received':        { icon: faDollarSign,         color: '#eb2f96' },
}

type DisplayStage = ComputedStage & {
  icon: IconDefinition
  color: string
}

type FunnelStageProps = {
  stage: DisplayStage
  showOverall: boolean
}

const FunnelStage = ({ stage, showOverall }: FunnelStageProps) => (
  <div className="funnel-stage">
    <div className="funnel-icon" style={{ backgroundColor: stage.color }}>
      <FontAwesomeIcon icon={stage.icon} />
    </div>
    <span className="funnel-count">{stage.count.toLocaleString()}</span>
    <span className="funnel-volume">
      {centsToDollarsCompact(stage.invoiceVolume)}
      {stage.invoiceVolume > 0 && (
        <span className="funnel-volume-take">
          ({centsToDollarsCompact(stage.invoiceVolume * 0.008)})
        </span>
      )}
    </span>
    <span className="funnel-label">{stage.stage}</span>
    {stage.stepConversion !== null && (
      <span className="funnel-conversion">
        <span className="funnel-conversion-step" style={{ color: stage.color }}>
          {percentageDisplay(stage.stepConversion)}
        </span>
        {showOverall && stage.overallConversion !== null && (
          <span className="funnel-conversion-overall">
            ({percentageDisplay(stage.overallConversion)})
          </span>
        )}
      </span>
    )}
  </div>
)

type FunnelArrowProps = {
  dropoff: number
}

const FunnelArrow = ({ dropoff }: FunnelArrowProps) => (
  <div className="funnel-arrow">
    <FontAwesomeIcon icon={faArrowRight} className="funnel-arrow-icon" />
    <span className="funnel-arrow-dropoff">-{dropoff.toLocaleString()}</span>
  </div>
)

const FunnelRow = ({ stages }: { stages: DisplayStage[] }) => (
  <div className="funnel-row">
    {stages.map((stage, i) => (
      <div key={stage.stage} style={{ display: 'flex', alignItems: 'flex-start' }}>
        {i > 0 && stage.dropoff !== null && <FunnelArrow dropoff={stage.dropoff} />}
        <FunnelStage stage={stage} showOverall={i >= 2} />
      </div>
    ))}
  </div>
)

const BUCKET_CONFIG: Record<string, { color: string, nickname: string }> = {
  '1–25%':   { color: '#faad14', nickname: 'Dabblers' },
  '26–50%':  { color: '#fa8c16', nickname: 'Partial' },
  '51–75%':  { color: '#1890ff', nickname: 'Majority' },
  '76–100%': { color: '#52c41a', nickname: 'All-in' },
}

const DEFAULT_BUCKET_COLOR = '#d9d9d9'

type BucketBarProps = {
  buckets: AdoptionDepthBucket[]
  label: string
}

const BucketBar = ({ buckets, label }: BucketBarProps) => {
  const segments = computeBucketSegments(buckets)
  return (
    <div className="depth-bucket-section">
      <Text type="secondary" className="depth-bucket-label">{label}</Text>
      <div className="depth-bucket-bar">
        {segments.map((seg) => {
          if (seg.pct === 0) {
            return null
          }
          const color = BUCKET_CONFIG[seg.label]?.color ?? DEFAULT_BUCKET_COLOR
          return (
            <div
              key={seg.label}
              className="depth-bucket-segment"
              style={{ width: `${seg.pct}%`, backgroundColor: color }}
              title={`${seg.label}: ${seg.tenantCount} tenants`}
            />
          )
        })}
      </div>
      <div className="depth-bucket-legend">
        {segments.map((seg) => {
          const config = BUCKET_CONFIG[seg.label]
          const color = config?.color ?? DEFAULT_BUCKET_COLOR
          return (
            <div key={seg.label} className="depth-bucket-legend-item">
              <span className="depth-bucket-legend-swatch" style={{ backgroundColor: color }} />
              <span className="depth-bucket-legend-text">
                {seg.label} {config && <Text type="secondary">({config.nickname})</Text>}
              </span>
              <span className="depth-bucket-legend-count">{seg.tenantCount}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

type AdoptionDepthPanelProps = {
  depth: AdoptionDepthData
}

const AdoptionDepthPanel = ({ depth }: AdoptionDepthPanelProps) => {
  const { headlines, invoiceCountBuckets, dollarVolumeBuckets } = depth
  return (
    <Card className="depth-card">
      <Title level={3} style={{ marginBottom: 24 }}>Adoption Depth</Title>

      <div className="depth-headlines">
        <div className="depth-headline">
          <span className="depth-headline-value">{headlines.adoptingTenantCount.toLocaleString()}</span>
          <span className="depth-headline-label">Adopting Tenants</span>
        </div>
        <div className="depth-headline">
          <span className="depth-headline-value">{percentageDisplay(headlines.invoiceCountSharePct)}</span>
          <span className="depth-headline-label">Invoice Count Share</span>
        </div>
        <div className="depth-headline">
          <span className="depth-headline-value">{percentageDisplay(headlines.dollarVolumeSharePct)}</span>
          <span className="depth-headline-label">Dollar Volume Share</span>
        </div>
      </div>

      <div className="depth-buckets">
        <BucketBar buckets={invoiceCountBuckets} label="By Invoice Count" />
        <BucketBar buckets={dollarVolumeBuckets} label="By Dollar Volume" />
      </div>
    </Card>
  )
}

const JOB_TYPE_BUCKET_COLORS: Record<string, string> = {
  DesignBuild: '#1890ff',
  Maintenance: '#52c41a',
  ServiceTicket: '#fa8c16',
  Unknown: '#8c8c8c',
}

type PaymentsByJobTypePanelProps = {
  data: PaymentsByJobTypeData
}

const PaymentsByJobTypePanel = ({ data }: PaymentsByJobTypePanelProps) => {
  const rows = useMemo(() => computePaymentsByJobTypeRows(data.buckets, data.totals), [data])
  return (
    <Card className="depth-card">
      <Title level={3} style={{ marginBottom: 24 }}>Payments by Job Type</Title>

      <div className="depth-headlines">
        <div className="depth-headline">
          <span className="depth-headline-value">{data.totals.paidInvoiceCount.toLocaleString()}</span>
          <span className="depth-headline-label">Paid Invoices</span>
        </div>
        <div className="depth-headline">
          <span className="depth-headline-value">
            {centsToDollarsCompact(data.totals.paidInvoiceVolume) ?? '$0'}
          </span>
          <span className="depth-headline-label">Paid Invoice Volume</span>
        </div>
      </div>

      <div className="jobtype-rows">
        {rows.map((row) => {
          const color = JOB_TYPE_BUCKET_COLORS[row.bucket] ?? '#d9d9d9'
          return (
            <div key={row.bucket} className="jobtype-row">
              <div className="jobtype-row-header">
                <span className="jobtype-row-swatch" style={{ backgroundColor: color }} />
                <span className="jobtype-row-label">{row.label}</span>
                <Tooltip title="Tenants with at least one paid invoice in this bucket. A tenant can appear in multiple buckets.">
                  <span className="jobtype-row-tenants">
                    {row.tenantCount.toLocaleString()} tenant{row.tenantCount === 1 ? '' : 's'}
                  </span>
                </Tooltip>
              </div>
              <div className="jobtype-row-metrics">
                <div className="jobtype-row-metric">
                  <span className="jobtype-row-metric-value">{row.paidInvoiceCount.toLocaleString()}</span>
                  <span className="jobtype-row-metric-label">
                    Paid Invoices
                    {row.countPct !== null && (
                      <Text type="secondary" style={{ marginLeft: 4 }}>
                        ({percentageDisplay(row.countPct)})
                      </Text>
                    )}
                  </span>
                  <div className="jobtype-row-bar">
                    <div
                      className="jobtype-row-bar-fill"
                      style={{ width: `${row.countPct ?? 0}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
                <div className="jobtype-row-metric">
                  <span className="jobtype-row-metric-value">
                    {centsToDollarsCompact(row.paidInvoiceVolume) ?? '$0'}
                  </span>
                  <span className="jobtype-row-metric-label">
                    Paid Volume
                    {row.volumePct !== null && (
                      <Text type="secondary" style={{ marginLeft: 4 }}>
                        ({percentageDisplay(row.volumePct)})
                      </Text>
                    )}
                  </span>
                  <div className="jobtype-row-bar">
                    <div
                      className="jobtype-row-bar-fill"
                      style={{ width: `${row.volumePct ?? 0}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

const periodOptions = Object.entries(FunnelPeriods).map(([value, label]) => ({ value, label }))

const InvoicingFunnel = () => {
  const [period, setPeriod] = useState<FunnelPeriod>('all')
  const { data, isLoading } = useInvoicingFunnel(period)
  const refreshMutation = useInvoicingFunnelRefresh()

  const displayStages: DisplayStage[] = useMemo(() => {
    if (!data?.adoptionFunnel?.stages) {
      return []
    }
    return computeStages(data.adoptionFunnel.stages).map((s) => {
      const config = STAGE_CONFIG[s.stage] ?? { icon: faUsers, color: '#8c8c8c' }
      return { ...s, icon: config.icon, color: config.color }
    })
  }, [data])

  const summary = useMemo(() => computeSummary(displayStages), [displayStages])

  if (isLoading || !data) {
    return <Skeleton active />
  }

  const { totalCount, approvedCount, approvedPercent, payingCount, payingPercent, totalDropoff } = summary

  return (
    <>
      <Card className="funnel-card">
        <div className="funnel-header">
          <div className="funnel-header-left">
            <Title level={3}>Invoicing Adoption Funnel</Title>
          </div>
          <div className="funnel-header-controls">
            <Select
              value={period}
              onChange={setPeriod}
              options={periodOptions}
              style={{ width: 130 }}
            />
            <Tooltip title="Refresh backend cache. Takes a few seconds.">
              <Button
                icon={<FontAwesomeIcon icon={faArrowsRotate} />}
                onClick={() => refreshMutation.mutate(undefined, {
                  onSuccess: () => notification.success({
                    message: 'Refresh triggered. New data will appear within a few seconds — reload to see it.',
                  }),
                  onError: () => notification.error({
                    message: 'Failed to trigger invoicing funnel refresh.',
                  }),
                })}
                loading={refreshMutation.isLoading}
              >
                Refresh
              </Button>
            </Tooltip>
          </div>
        </div>

        <FunnelRow stages={displayStages} />

        <div className="funnel-footer">
          <div className="funnel-footer-metric">
            <span className="funnel-footer-metric-value">{totalCount.toLocaleString()}</span>
            <span className="funnel-footer-metric-label">Total Tenants</span>
          </div>
          <div className="funnel-footer-metric">
            <span className="funnel-footer-metric-value">
              {approvedCount.toLocaleString()}
              {approvedPercent !== null && (
                <Text type="secondary" style={{ fontSize: 14, marginLeft: 4 }}>
                  ({percentageDisplay(approvedPercent)})
                </Text>
              )}
            </span>
            <span className="funnel-footer-metric-label">Approved Merchants</span>
          </div>
          <div className="funnel-footer-metric">
            <span className="funnel-footer-metric-value">
              {payingCount.toLocaleString()}
              {payingPercent !== null && (
                <Text type="secondary" style={{ fontSize: 14, marginLeft: 4 }}>
                  ({percentageDisplay(payingPercent)})
                </Text>
              )}
            </span>
            <span className="funnel-footer-metric-label">Active Payers</span>
          </div>
          <div className="funnel-footer-metric">
            <span className="funnel-footer-metric-value">{totalDropoff.toLocaleString()}</span>
            <span className="funnel-footer-metric-label">Total Dropoff</span>
          </div>
        </div>
      </Card>

      {data.adoptionDepth && <AdoptionDepthPanel depth={data.adoptionDepth} />}

      {data.paymentsByJobType && <PaymentsByJobTypePanel data={data.paymentsByJobType} />}
    </>
  )
}

export default InvoicingFunnel
